import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import {
  Alert,
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import api from '../services/api';
import moment from 'moment';
import {
  Delete,
  Download,
  Visibility,
  Storage,
  Schedule,
  CompareArrows,
  Clear,
  Search,
  NavigateBefore,
  NavigateNext
} from '@mui/icons-material';
import fileDownload from 'js-file-download';
import { useAuth } from "../components/AuthProvider"
import { useAuth as useAuthAdmin } from "../components/AuthAdminProvider"
import CodeMirror from '@uiw/react-codemirror';
import { EditorView } from '@codemirror/view';
import DiffRenderer from './DiffRenderer';
import DiffMiniMap from './DiffMiniMap';

export function BackupDialog({ open, id, handlerClose, admin = false }) {

  const [handlerSnack, setHandlerSnack] = React.useState()
  const routeBase = admin ? '/admin/hermes/backup' : '/hermes/backup'
  const [viewMode, setViewMode] = React.useState('single')
  const [compareId, setCompareId] = React.useState('')
  const [showAllVersions, setShowAllVersions] = React.useState(false)

  // Diff state (from backend with pagination)
  const [diffData, setDiffData] = React.useState([])
  const [diffStats, setDiffStats] = React.useState(null)
  const [diffLoading, setDiffLoading] = React.useState(false)
  const [diffLoadingMore, setDiffLoadingMore] = React.useState(false)
  const [diffError, setDiffError] = React.useState(null)
  const [diffHasMore, setDiffHasMore] = React.useState(false)
  const [diffTotalLines, setDiffTotalLines] = React.useState(0)
  const [diffOffset, setDiffOffset] = React.useState(0)

  // Search state with debounce
  const [searchQuery, setSearchQuery] = React.useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('')
  const [searchResults, setSearchResults] = React.useState([])
  const [searchLoading, setSearchLoading] = React.useState(false)
  const [currentMatchIndex, setCurrentMatchIndex] = React.useState(0)
  const [highlightLineIndex, setHighlightLineIndex] = React.useState(null)
  const searchDebounceRef = React.useRef(null)

  // Scroll control
  const [scrollToIndex, setScrollToIndex] = React.useState(undefined)
  const codeMirrorRef = React.useRef(null)

  // Single mode search state with debounce
  const [singleSearchQuery, setSingleSearchQuery] = React.useState('')
  const [debouncedSingleSearchQuery, setDebouncedSingleSearchQuery] = React.useState('')
  const [singleSearchResults, setSingleSearchResults] = React.useState([])
  const [singleSearchLoading, setSingleSearchLoading] = React.useState(false)
  const [singleCurrentMatchIndex, setSingleCurrentMatchIndex] = React.useState(0)
  const singleSearchDebounceRef = React.useRef(null)


  const item = {
    backup: {
      host_name: '',
      ip: '',
      vendor_name: '',
      type_name: '',
      id: 0,
      content: '',
      host_id: 0
    },
    list: [],
  }
  const [state, setState] = React.useState(item);

  const auth = !admin ? useAuth() : useAuthAdmin()

  // Reset all state when dialog opens
  React.useEffect(() => {
    if (id > 0 && open) {
      getEditInfo(id)
      setViewMode('single')
      setCompareId('')
      resetDiffState()
      resetSearchState()
      resetSingleSearchState()
      setShowAllVersions(false)
    }
  }, [id, open])

  const resetDiffState = () => {
    setDiffData([])
    setDiffStats(null)
    setDiffError(null)
    setDiffHasMore(false)
    setDiffTotalLines(0)
    setDiffOffset(0)
  }

  const resetSearchState = () => {
    setSearchQuery('')
    setSearchResults([])
    setCurrentMatchIndex(0)
    setHighlightLineIndex(null)
  }

  const resetSingleSearchState = () => {
    setSingleSearchQuery('')
    setSingleSearchResults([])
    setSingleCurrentMatchIndex(0)
  }

  const getEditInfo = (id) => {
    if (!id) return
    api.get(`${routeBase}/${id}`)
      .then(res => {
        setState(res.data.data)
      }).catch(() => setState(item))
  }

  // Load diff from backend (paginated)
  const loadDiff = React.useCallback((backupId, compareToId, offset = 0, append = false) => {
    if (!backupId || !compareToId) {
      resetDiffState()
      return
    }

    if (offset === 0) {
      setDiffLoading(true)
    } else {
      setDiffLoadingMore(true)
    }
    setDiffError(null)

    api.get(`${routeBase}/${backupId}/diff/${compareToId}?offset=${offset}&limit=500`)
      .then(res => {
        const data = res.data.data
        if (append) {
          setDiffData(prev => [...prev, ...data.diff])
        } else {
          setDiffData(data.diff)
        }
        setDiffStats(data.stats)
        setDiffHasMore(data.has_more)
        setDiffTotalLines(data.total_lines)
        setDiffOffset(offset + data.diff.length)
        setDiffLoading(false)
        setDiffLoadingMore(false)
      })
      .catch(err => {
        setDiffError(err.response?.data?.detail || 'Erro ao carregar diff')
        setDiffLoading(false)
        setDiffLoadingMore(false)
      })
  }, [routeBase])

  // Load more diff lines
  const loadMoreDiff = React.useCallback(() => {
    if (diffLoadingMore || !diffHasMore || !state?.backup?.id || !compareId) return
    loadDiff(state.backup.id, compareId, diffOffset, true)
  }, [diffLoadingMore, diffHasMore, state?.backup?.id, compareId, diffOffset, loadDiff])

  // Load diff when compare selection changes
  React.useEffect(() => {
    if (viewMode === 'diff' && compareId && state?.backup?.id) {
      resetSearchState()
      loadDiff(state.backup.id, compareId, 0, false)
    }
  }, [compareId, state?.backup?.id, viewMode, loadDiff])

  // Debounce for diff search
  React.useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
      }
    }
  }, [searchQuery])

  // Debounce for single search
  React.useEffect(() => {
    if (singleSearchDebounceRef.current) {
      clearTimeout(singleSearchDebounceRef.current)
    }
    singleSearchDebounceRef.current = setTimeout(() => {
      setDebouncedSingleSearchQuery(singleSearchQuery)
    }, 300)
    return () => {
      if (singleSearchDebounceRef.current) {
        clearTimeout(singleSearchDebounceRef.current)
      }
    }
  }, [singleSearchQuery])

  // Auto-search when debounced query changes (diff mode)
  React.useEffect(() => {
    if (!debouncedSearchQuery.trim() || !state?.backup?.id || !compareId) {
      if (!debouncedSearchQuery.trim()) {
        setSearchResults([])
        setHighlightLineIndex(null)
      }
      return
    }

    setSearchLoading(true)
    api.get(`${routeBase}/${state.backup.id}/diff/${compareId}/search?q=${encodeURIComponent(debouncedSearchQuery)}&limit=500`)
      .then(res => {
        const matches = res.data.data.matches || []
        setSearchResults(matches)
        setCurrentMatchIndex(0)
        setSearchLoading(false)

        // Jump to first match
        if (matches.length > 0) {
          jumpToMatch(matches[0], 0)
        } else {
          setHighlightLineIndex(null)
        }
      })
      .catch(() => {
        setSearchResults([])
        setSearchLoading(false)
      })
  }, [debouncedSearchQuery, state?.backup?.id, compareId, routeBase])

  // Jump to a specific match
  const jumpToMatch = React.useCallback((match, index) => {
    if (!match) return

    setCurrentMatchIndex(index)

    // Check if match is in current loaded data
    const matchInCurrentData = diffData.findIndex((_, i) => i === match.line_index)

    if (match.line_index < diffOffset && matchInCurrentData >= 0) {
      // Match is in loaded data, just scroll to it
      setHighlightLineIndex(match.line_index)
      setScrollToIndex(match.line_index)
      setTimeout(() => setScrollToIndex(undefined), 100)
    } else {
      // Need to load data around this match
      api.get(`${routeBase}/${state.backup.id}/diff/${compareId}/around?line_index=${match.line_index}&context=250`)
        .then(res => {
          const data = res.data.data
          setDiffData(data.diff)
          setDiffStats(data.stats)
          setDiffHasMore(data.has_more)
          setDiffTotalLines(data.total_lines)
          setDiffOffset(data.offset + data.diff.length)

          // Find the match in the new data
          const localIndex = match.line_index - data.offset
          setHighlightLineIndex(localIndex)
          setScrollToIndex(localIndex)
          setTimeout(() => setScrollToIndex(undefined), 100)
        })
        .catch(() => { })
    }
  }, [diffData, diffOffset, routeBase, state?.backup?.id, compareId])

  // Navigate between search results
  const goToNextMatch = () => {
    if (searchResults.length === 0) return
    const nextIndex = (currentMatchIndex + 1) % searchResults.length
    jumpToMatch(searchResults[nextIndex], nextIndex)
  }

  const goToPrevMatch = () => {
    if (searchResults.length === 0) return
    const prevIndex = (currentMatchIndex - 1 + searchResults.length) % searchResults.length
    jumpToMatch(searchResults[prevIndex], prevIndex)
  }

  // Auto-search when debounced query changes (single mode)
  React.useEffect(() => {
    if (!debouncedSingleSearchQuery.trim() || !state?.backup?.id) {
      if (!debouncedSingleSearchQuery.trim()) {
        setSingleSearchResults([])
      }
      return
    }

    setSingleSearchLoading(true)
    api.get(`${routeBase}/${state.backup.id}/search?q=${encodeURIComponent(debouncedSingleSearchQuery)}`)
      .then(res => {
        const matches = res.data.data.matches || []
        setSingleSearchResults(matches)
        setSingleCurrentMatchIndex(0)
        setSingleSearchLoading(false)

        // Jump to first match in CodeMirror
        if (matches.length > 0) {
          jumpToSingleMatch(matches[0].line_number)
        }
      })
      .catch(() => {
        setSingleSearchResults([])
        setSingleSearchLoading(false)
      })
  }, [debouncedSingleSearchQuery, state?.backup?.id, routeBase])

  // Jump to line in CodeMirror (single mode)
  const jumpToSingleMatch = (lineNumber) => {
    if (codeMirrorRef.current?.view) {
      const view = codeMirrorRef.current.view
      const line = view.state.doc.line(lineNumber)
      view.dispatch({
        selection: { anchor: line.from },
        scrollIntoView: true
      })
    }
  }

  const goToNextSingleMatch = () => {
    if (singleSearchResults.length === 0) return
    const nextIndex = (singleCurrentMatchIndex + 1) % singleSearchResults.length
    setSingleCurrentMatchIndex(nextIndex)
    jumpToSingleMatch(singleSearchResults[nextIndex].line_number)
  }

  const goToPrevSingleMatch = () => {
    if (singleSearchResults.length === 0) return
    const prevIndex = (singleCurrentMatchIndex - 1 + singleSearchResults.length) % singleSearchResults.length
    setSingleCurrentMatchIndex(prevIndex)
    jumpToSingleMatch(singleSearchResults[prevIndex].line_number)
  }

  const versionList = state.list

  const showNotify = (msg, sev = "success") => {
    setMsgSnack(msg)
    setSevSnack(sev)
    setHandlerSnack(true)
  }

  const [msgSnack, setMsgSnack] = React.useState('')
  const [sevSnack, setSevSnack] = React.useState('')

  // Handle click on minimap block
  const handleMinimapClick = (lineIndex) => {
    // diffData starts at (diffOffset - diffData.length) and goes to diffOffset
    const dataStartOffset = diffOffset - diffData.length
    const dataEndOffset = diffOffset

    const lineInRange = lineIndex >= dataStartOffset && lineIndex < dataEndOffset

    if (!lineInRange) {
      // Need to load data around this line
      api.get(`${routeBase}/${state.backup.id}/diff/${compareId}/around?line_index=${lineIndex}&context=250`)
        .then(res => {
          const data = res.data.data
          setDiffData(data.diff)
          setDiffOffset(data.offset + data.diff.length)
          setDiffHasMore(data.has_more)

          // Calculate local index and scroll
          const localIndex = lineIndex - data.offset
          setScrollToIndex(localIndex)
          setTimeout(() => setScrollToIndex(undefined), 100)
        })
    } else {
      // Line is already loaded, just scroll
      const localIndex = lineIndex - dataStartOffset
      setScrollToIndex(localIndex)
      setTimeout(() => setScrollToIndex(undefined), 100)
    }
  }

  // CodeMirror theme
  const lightTheme = EditorView.theme({
    "&": { backgroundColor: "#fafafa", color: "#1a1a1a" },
    ".cm-content": {
      caretColor: "#1a1a1a",
      fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
      fontSize: "13px",
      lineHeight: "1.6",
    },
    ".cm-gutters": {
      backgroundColor: "#f0f0f0",
      color: "#666",
      border: "none",
      borderRight: "1px solid #ddd",
    },
    ".cm-lineNumbers .cm-gutterElement": { padding: "0 8px 0 4px" },
    ".cm-activeLine": { backgroundColor: "#e8f4fc" },
    ".cm-activeLineGutter": { backgroundColor: "#e0e0e0" },
    ".cm-selectionBackground": { backgroundColor: "#b3d9ff" },
    "&.cm-focused .cm-selectionBackground": { backgroundColor: "#b3d9ff" },
  }, { dark: false });

  return (
    <div>
      <Snack open={handlerSnack} message={msgSnack} severity={sevSnack} handlerSnack={() => setHandlerSnack(false)} />
      <Dialog
        open={open}
        fullWidth={true}
        scroll={'paper'}
        maxWidth='xl'
        PaperProps={{ sx: { bgcolor: '#fafafa', minHeight: '80vh' } }}
      >
        <DialogTitle sx={{ bgcolor: '#fff', borderBottom: '1px solid #e0e0e0', pb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Storage color="primary" />
                <Typography variant='h6' fontWeight="bold" color='text.primary'>
                  {state?.backup?.host_name}
                </Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip size="small" label={state?.backup?.ip} variant="outlined" color="default" />
                <Chip size="small" label={state?.backup?.vendor_name} color="primary" variant="outlined" />
                <Chip size="small" label={state?.backup?.type_name} color="secondary" variant="outlined" />
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, val) => {
                    if (val) {
                      setViewMode(val)
                      if (val === 'single') {
                        setCompareId('')
                        resetDiffState()
                        resetSearchState()
                      } else if (val === 'diff') {
                        // Auto-select previous backup for comparison
                        const currentBackupId = state?.backup?.id
                        const currentBackupDate = state.list.find(x => x.id === currentBackupId)?.created_at
                        if (currentBackupDate) {
                          // Find backups older than current
                          const olderBackups = versionList.filter(b =>
                            b.id !== currentBackupId &&
                            new Date(b.created_at) < new Date(currentBackupDate)
                          )
                          // Select the most recent one (first in the filtered list since it's ordered desc)
                          if (olderBackups.length > 0) {
                            setCompareId(olderBackups[0].id)
                          }
                        }
                      }
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="single">
                    <Tooltip title="Visualizar"><Visibility /></Tooltip>
                  </ToggleButton>
                  <ToggleButton value="diff">
                    <Tooltip title="Comparar"><CompareArrows /></Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>

                {viewMode === 'single' ? (
                  <FormControl size='small' sx={{ minWidth: 200 }}>
                    <InputLabel>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Schedule fontSize="small" /> Versão
                      </Box>
                    </InputLabel>
                    <Select
                      size="small"
                      label="Versão select"
                      value={state?.backup?.id || ''}
                      onChange={(e) => getEditInfo(e.target.value)}
                      sx={{ bgcolor: '#fff' }}
                    >
                      {(showAllVersions ? versionList : versionList.slice(0, 10)).map((backup) =>
                        <MenuItem key={backup.id} value={backup.id}>
                          {moment(backup.created_at).format('DD/MM/YYYY HH:mm')}
                        </MenuItem>
                      )}
                      {!showAllVersions && versionList.length > 10 && (
                        <MenuItem onClick={(e) => { e.stopPropagation(); setShowAllVersions(true) }}
                          sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                          Ver todos ({versionList.length} versões)
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                ) : (
                  <>
                    <FormControl size='small' sx={{ minWidth: 180 }}>
                      <InputLabel sx={{ color: '#cf222e' }}>Anterior</InputLabel>
                      <Select
                        size="small"
                        label="Anterior"
                        value={compareId}
                        onChange={(e) => setCompareId(e.target.value)}
                        sx={{ bgcolor: '#ffebe9', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cf222e' } }}
                      >
                        <MenuItem value="">Selecione...</MenuItem>
                        {versionList
                          .filter(b => b.id !== state?.backup?.id && new Date(b.created_at) < new Date(state.list.find(x => x.id === state?.backup?.id)?.created_at || 0))
                          .slice(0, showAllVersions ? undefined : 10)
                          .map((backup) =>
                            <MenuItem key={backup.id} value={backup.id}>
                              {moment(backup.created_at).format('DD/MM/YYYY HH:mm')}
                            </MenuItem>
                          )}
                      </Select>
                    </FormControl>
                    <CompareArrows sx={{ color: '#666' }} />
                    <FormControl size='small' sx={{ minWidth: 180 }}>
                      <InputLabel sx={{ color: '#1a7f37' }}>Atual</InputLabel>
                      <Select
                        size="small"
                        label="Atual"
                        value={state?.backup?.id || ''}
                        onChange={(e) => getEditInfo(e.target.value)}
                        sx={{ bgcolor: '#e6ffec', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1a7f37' } }}
                      >
                        {(showAllVersions ? versionList : versionList.slice(0, 10)).map((backup) =>
                          <MenuItem key={backup.id} value={backup.id}>
                            {moment(backup.created_at).format('DD/MM/YYYY HH:mm')}
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </>
                )}

                <Box flexGrow={1} />

                <Tooltip title="Baixar backup">
                  <IconButton
                    disabled={!auth.hasPerm('backup.get_all')}
                    onClick={() => {
                      api.get(`${routeBase}/download/${state?.backup?.id}`).then(res => {
                        fileDownload(res.data, `${state?.backup?.host_name} - ${state?.backup?.ip} - ${moment(state?.backup?.created_at).format('DD-MM-YYYY HH:mm:ss')}.txt`);
                      })
                    }}
                    sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&:disabled': { bgcolor: '#ccc' } }}
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Apagar backup">
                  <IconButton
                    disabled={!auth.hasPerm('backup.delete')}
                    onClick={() => {
                      if (!window.confirm('Deseja apagar o backup?')) return
                      api.delete(`${routeBase}/${state.backup.id}`).then(() => {
                        handlerClose()
                        showNotify('Backup apagado com sucesso')
                      }).catch(() => showNotify('Erro ao apagar backup', 'error'))
                    }}
                    sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, '&:disabled': { bgcolor: '#ccc' } }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: '#fafafa' }}>
          {viewMode === 'single' ? (
            <Box sx={{ m: 2 }}>
              {/* Search Bar for Single Mode - with debounce */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <TextField
                  size="small"
                  placeholder="Buscar no backup..."
                  value={singleSearchQuery}
                  onChange={(e) => setSingleSearchQuery(e.target.value)}
                  sx={{ width: 280 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {singleSearchLoading ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Search fontSize="small" color="action" />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                {singleSearchResults.length > 0 && (
                  <>
                    <Box sx={{ mx: 1, color: 'text.secondary' }}>
                      <Typography variant="body2">
                        {singleCurrentMatchIndex + 1} / {singleSearchResults.length}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={goToPrevSingleMatch}>
                      <NavigateBefore />
                    </IconButton>
                    <IconButton size="small" onClick={goToNextSingleMatch}>
                      <NavigateNext />
                    </IconButton>
                    <IconButton size="small" onClick={resetSingleSearchState}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </>
                )}

                {singleSearchQuery && singleSearchResults.length === 0 && !singleSearchLoading && (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum resultado
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 0 }}>
                {/* CodeMirror */}
                <Box sx={{ flex: 1, border: '1px solid #e0e0e0', borderRadius: 0, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <CodeMirror
                    ref={codeMirrorRef}
                    value={state?.backup?.content || ''}
                    editable={false}
                    extensions={[lightTheme]}
                    basicSetup={{ lineNumbers: true, highlightActiveLineGutter: true, highlightActiveLine: true, foldGutter: false }}
                    style={{ minHeight: '400px', maxHeight: 'calc(65vh - 80px)', overflow: 'auto' }}
                  />
                </Box>

                {/* Search Results List */}
                {singleSearchResults.length > 0 && (
                  <Box
                    sx={{
                      width: 280,
                      minWidth: 280,
                      height: 'calc(65vh - 80px)',
                      bgcolor: '#fff',
                      borderLeft: '1px solid #e0e0e0',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header */}
                    <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Resultados
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {singleSearchResults.length} ocorrências
                      </Typography>
                    </Box>

                    {/* Results list */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 0.5 }}>
                      {singleSearchResults.map((match, index) => (
                        <Box
                          key={`${match.line_number}-${index}`}
                          onClick={() => {
                            setSingleCurrentMatchIndex(index)
                            jumpToSingleMatch(match.line_number)
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            p: 0.75,
                            mb: 0.5,
                            borderRadius: 1,
                            cursor: 'pointer',
                            bgcolor: index === singleCurrentMatchIndex ? '#e3f2fd' : '#fafafa',
                            border: index === singleCurrentMatchIndex ? '1px solid #2196f3' : '1px solid transparent',
                            '&:hover': {
                              bgcolor: index === singleCurrentMatchIndex ? '#e3f2fd' : '#f0f0f0',
                            },
                          }}
                        >
                          {/* Line number */}
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "'Fira Code', 'Consolas', monospace",
                              fontSize: '10px',
                              color: '#666',
                              bgcolor: '#e0e0e0',
                              px: 0.5,
                              borderRadius: 0.5,
                              minWidth: 35,
                              textAlign: 'right',
                            }}
                          >
                            {match.line_number}
                          </Typography>

                          {/* Content preview */}
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "'Fira Code', 'Consolas', monospace",
                              fontSize: '11px',
                              color: '#333',
                              flex: 1,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {match.content?.substring(0, 60) || '(vazio)'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ m: 2 }}>
              {!compareId ? (
                <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <CompareArrows sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography>Selecione uma versão para comparar</Typography>
                </Box>
              ) : diffError ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Alert severity="error">{diffError}</Alert>
                </Box>
              ) : (
                <>
                  {/* Search Bar - with debounce */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                    <TextField
                      size="small"
                      placeholder="Buscar no diff..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      sx={{ width: 280 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {searchLoading ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Search fontSize="small" color="action" />
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />

                    {searchResults.length > 0 && (
                      <>
                        <Box sx={{ mx: 1, color: 'text.secondary' }}>
                          <Typography variant="body2">
                            {currentMatchIndex + 1} / {searchResults.length}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={goToPrevMatch}>
                          <NavigateBefore />
                        </IconButton>
                        <IconButton size="small" onClick={goToNextMatch}>
                          <NavigateNext />
                        </IconButton>
                        <IconButton size="small" onClick={resetSearchState}>
                          <Clear fontSize="small" />
                        </IconButton>
                      </>
                    )}

                    {searchQuery && searchResults.length === 0 && !searchLoading && (
                      <Typography variant="body2" color="text.secondary">
                        Nenhum resultado
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0 }}>
                    {/* Diff Renderer */}
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                      <DiffRenderer
                        diff={diffData}
                        loading={diffLoading}
                        loadingMore={diffLoadingMore}
                        onLoadMore={loadMoreDiff}
                        scrollToIndex={scrollToIndex}
                        height="calc(65vh - 80px)"
                        hasMore={diffHasMore}
                        totalLines={diffTotalLines}
                        highlightIndex={highlightLineIndex}
                        searchQuery={searchQuery}
                      />
                    </Box>

                    {/* Minimap - Change blocks list */}
                    {compareId && !diffLoading && (
                      <DiffMiniMap
                        backupId={state?.backup?.id}
                        compareId={compareId}
                        height="calc(65vh - 80px)"
                        onClickBlock={handleMinimapClick}
                        stats={diffStats || {}}
                        api={api}
                        routeBase={routeBase}
                      />
                    )}
                  </Box>

                  {/* Stats bar */}
                  {diffStats && !diffLoading && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 2, p: 1.5, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>{diffStats.total_old || 0}</strong> linhas (anterior)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>{diffStats.total_new || 0}</strong> linhas (atual)
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1a7f37' }}>
                        <strong>+{diffStats.added || 0}</strong> adicionadas
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#cf222e' }}>
                        <strong>-{diffStats.removed || 0}</strong> removidas
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>{diffStats.unchanged || 0}</strong> inalteradas
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ bgcolor: '#fff', borderTop: '1px solid #e0e0e0', p: 2 }}>
          <Button variant="outlined" onClick={handlerClose}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

const Snack = (props) => {
  return (
    <Snackbar
      open={props.open}
      autoHideDuration={6000}
      onClose={() => { props.handlerSnack() }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={() => { props.handlerSnack() }} severity={props.severity || 'error'} sx={{ width: '100%' }}>
        {props.message}
      </Alert>
    </Snackbar>
  )
}

export default function BackupViewButton({ id, admin = false }) {
  const [open, setOpen] = React.useState(false)
  return (
    <>
      <IconButton onClick={() => setOpen(true)}><Visibility /></IconButton>
      <BackupDialog admin={admin} open={open} id={id} handlerClose={() => setOpen(false)} />
    </>
  )
}
